import { CommonEvents } from "@common/services/common-events";
import type { SharedNotesStoreDependencies } from "@common/stores/shared-notes-store";
import SharedNotesStore from "@common/stores/shared-notes-store";
import type { SpaceId } from "@common/types/space";
import type { TodoId } from "@common/types/todo";
import { action, computed, makeObservable, observable, runInAction } from "mobx";

export default class NotesStore extends SharedNotesStore {
  @observable
  isLoading = false;

  @observable
  currentTodoId: TodoId | null = null;
  @observable
  currentSpaceId: SpaceId | null = null;

  @computed
  get currentNote() {
    return this.notesCache[this.currentTodoId ?? ""];
  }

  @computed
  get isVisible() {
    return Boolean(this.currentTodoId);
  }

  @computed
  get isSaving() {
    return this.currentNote && this.currentNote._lastSaveTime < this.currentNote._lastEditTime;
  }

  @computed
  get currentNoteTodo() {
    if (!this.currentSpaceId) return null;

    const todo = this.deps.spaceStore.todoStoreMap[this.currentSpaceId ?? ""]?.todoIdMap[this.currentTodoId ?? ""];

    return todo;
  }

  constructor(dependencies: SharedNotesStoreDependencies) {
    super(dependencies);
    makeObservable(this);
  }

  @action
  async setEditingNoteTodoId(todoId: TodoId | null, spaceId: string | null) {
    if (!(todoId && spaceId)) {
      this.currentTodoId = null;
      this.currentSpaceId = null;
      return;
    }

    this.currentTodoId = todoId;
    this.currentSpaceId = spaceId;

    runInAction(() => {
      this.isLoading = true;
    });

    try {
      // flush queue in case todo is not yet created
      await this.deps.todoService.dispatchUpdates();
      await this.openNoteForTodo(todoId, spaceId);
    } catch (e) {
      const messages: Record<string, string> = {
        NOTE_NOT_FOUND: "Note not found",
        CREATE_NOTE_FAILED: "Create Note Failed",
      };

      const message =
        messages[e as string] ?? (e as Error).message ?? (typeof e === "string" ? (e as string) : "Unknown Error");
      CommonEvents.emit("PUBLISH_TOAST", {
        title: "Error",
        content: message,
        titleColor: "negative",
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action
  async deleteCurrentNote() {
    if (!this.currentNoteTodo) return;
    const { success } = await this.deps.todoService.deleteNoteForTodo(this.currentNoteTodo.id);
    if (success) {
      runInAction(() => {
        if (!this.currentNoteTodo) return;
        this.currentNoteTodo.notes_id = null;
        delete this.notesCache[this.currentNoteTodo.id];
      });
    }
  }
}
